import { sessionStore } from '../domain/session/sessionStore';
import { refreshTokenService } from '../api/service/session';
import * as tokenStorage from '../domain/session/tokenStorage';
import { decodeAuthTokenClaims } from '../domain/session/tokenClaims';
import type { CredentialEntry, PersistedCredentialEntry, ScopeKey } from '../domain/session/types';

// One in-flight refresh per scope: a burst of 401s from the SAME scope coalesces
// onto one network call, while refreshes for different scopes never block or
// overwrite each other (`compareAndSetCredential` is the per-entry CAS guard).
// This only coalesces calls made in THIS tab — `withCrossTabLock` below is what
// serializes refreshes across tabs, which each hold their own copy of this map.
const inFlightByScope = new Map<ScopeKey, Promise<string>>();

// The backend revokes a session's tokens (access + refresh) as a pair on every
// refresh/login, keyed by company — not by device. So two tabs refreshing the same
// scope at once is a race with a loser, not two independent successes. Web Locks
// serialize that race across tabs (and processes); `navigator.locks` is absent in
// jsdom and older browsers, so fall back to running inline there.
const withCrossTabLock = <T>(scopeKey: ScopeKey, fn: () => Promise<T>): Promise<T> => {
	const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
	if (!locks) return fn();
	return locks.request(`auth-refresh:${scopeKey}`, fn);
};

// Another tab may have already refreshed this scope and persisted the result — either
// before we asked for the lock, or while we were waiting for it. `persist()` is
// last-writer-wins, so storage can also hold a STALE write-back from a tab that lost a
// past race; only trust it when `acquiredAt` is strictly newer than what we hold, and
// the tokens actually differ (an equal-but-newer-looking write is just our own).
const readNewerPersistedCredential = (
	scopeKey: ScopeKey,
	currentEntry: CredentialEntry,
): PersistedCredentialEntry | null => {
	const candidate = tokenStorage.readV2()?.entries[scopeKey];
	if (!candidate || candidate.acquiredAt <= currentEntry.acquiredAt) return null;
	if (
		candidate.accessToken === currentEntry.accessToken &&
		candidate.refreshToken === currentEntry.refreshToken
	) {
		return null;
	}
	// ScopeKey only encodes company + delegations, never the user — another tab may have
	// logged out and a DIFFERENT user logged into the same company, persisting under the
	// same scopeKey. Only adopt when both refresh tokens decode to the same non-null
	// subject; fail closed (null subject either side) to the network refresh/eviction path.
	const candidateSubject = decodeAuthTokenClaims(candidate.refreshToken).subject;
	const currentSubject = decodeAuthTokenClaims(currentEntry.refreshToken).subject;
	if (!candidateSubject || candidateSubject !== currentSubject) return null;
	return candidate;
};

const adoptPersistedCredential = (
	scopeKey: ScopeKey,
	currentEntry: CredentialEntry,
	persisted: PersistedCredentialEntry,
): string | null => {
	const committed = sessionStore
		.getState()
		.compareAndSetCredential(scopeKey, currentEntry.revision, {
			accessToken: persisted.accessToken,
			refreshToken: persisted.refreshToken,
			idToken: currentEntry.idToken,
		});
	if (committed) return persisted.accessToken;
	return sessionStore.getState().entries[scopeKey]?.accessToken ?? null;
};

const runRefresh = (scopeKey: ScopeKey): Promise<string> =>
	withCrossTabLock(scopeKey, async () => {
		// Read fresh: another tab may have changed this scope's entry while we
		// waited for the lock.
		const { entries, activeScopeKey } = sessionStore.getState();
		const entry = entries[scopeKey];
		if (!entry) throw new Error(`No cached credentials for scope ${scopeKey}`);
		// Only the active scope is refreshable. The entries of scopes visited earlier
		// survive in the map now, but their refresh tokens were revoked when the
		// current credential was issued — spending a network call on one only ends in
		// `evictScope`, which ends the session the user is actually in.
		if (scopeKey !== activeScopeKey) {
			throw new Error(`Scope ${scopeKey} is no longer the active scope`);
		}

		const persistedBeforeRefresh = readNewerPersistedCredential(scopeKey, entry);
		if (persistedBeforeRefresh) {
			const adopted = adoptPersistedCredential(scopeKey, entry, persistedBeforeRefresh);
			if (adopted) return adopted;
		}

		let response: { access_token: string; refresh_token: string };
		try {
			response = await refreshTokenService(entry.refreshToken);
		} catch (error) {
			// Another tab may have won this refresh while ours was in flight (e.g. it
			// held the lock first and its result landed between our check above and
			// this rejection) — adopt it instead of ending a session that is actually
			// still alive elsewhere.
			const persistedAfterFailure = readNewerPersistedCredential(scopeKey, entry);
			const adopted =
				persistedAfterFailure && adoptPersistedCredential(scopeKey, entry, persistedAfterFailure);
			if (adopted) return adopted;

			// Rejected and nothing newer in storage: the session is dead. `evictScope`
			// leaves it unauthenticated; the Axios interceptor owns the reset + redirect.
			sessionStore.getState().evictScope(scopeKey);
			throw error;
		}

		const committed = sessionStore.getState().compareAndSetCredential(scopeKey, entry.revision, {
			accessToken: response.access_token,
			refreshToken: response.refresh_token,
			idToken: entry.idToken,
		});
		if (committed) return response.access_token;

		// CAS lost the race to a concurrent update of the SAME scope (e.g. a fresh
		// acquisition landed first) — reuse whatever token is now cached rather than
		// discarding a still-valid refresh outcome.
		const current = sessionStore.getState().entries[scopeKey];
		if (current) return current.accessToken;
		throw new Error(`Scope ${scopeKey} was evicted during refresh`);
	});

export const refreshAuthSession = (scopeKey: ScopeKey): Promise<string> => {
	let inFlight = inFlightByScope.get(scopeKey);
	if (!inFlight) {
		inFlight = runRefresh(scopeKey).finally(() => {
			inFlightByScope.delete(scopeKey);
		});
		inFlightByScope.set(scopeKey, inFlight);
	}
	return inFlight;
};
